import * as THREE from "three";

import { SceneManager } from "../core/scene_manager.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import type { LevelEntity } from "../types/level_entity.js";
import type { MineHandle } from "../cumulative_boosters/boosters/mine_booster.js";
import type { InterpolationSink } from "../physics/physics_interpolation.js";

enum StickyMinePhase { Flying, Armed, Exploded }

export interface StickyMineOptions
{
  start: THREE.Vector3;
  target: LevelEntity;
  diameter: number;
  fuse_seconds: number;
  /** Spin direction multiplier (-1 or 1). */
  arc_side: number;
  on_explode: ( mine: StickyMine, target: LevelEntity | null, point: THREE.Vector3 ) => void;
}

/**
 * Level-start mine: flies from the cannon to a block, sticks to the face that
 * looks at the camera, follows the block while it blinks with an accelerating
 * rhythm and finally reports its explosion to the game screen.
 */
export class StickyMine implements MineHandle
{
  private static readonly FLIGHT_SECONDS = 0.75;
  private static readonly ARC_HEIGHT = 3;
  private static readonly FLIGHT_TURNS = 2;
  private static readonly SURFACE_GAP = 0.02;
  private static readonly AXIS_EPSILON = 1e-6;
  private static readonly FORWARD = new THREE.Vector3( 0, 0, 1 );
  private static readonly BLINK_MIN_HZ = 2;
  private static readonly BLINK_MAX_HZ = 9;
  private static readonly EMISSIVE_REST = 0.15;
  private static readonly EMISSIVE_PEAK = 2.4;
  private static readonly PULSE_SCALE = 0.1;
  private static readonly LANDING_SQUASH_SECONDS = 0.18;

  private static shared_material: THREE.MeshStandardMaterial | null = null;

  private readonly root = Object.assign( new THREE.Group(), { userData: { noBatch: true } } );
  private readonly material: THREE.MeshStandardMaterial;
  private readonly target: LevelEntity;
  private readonly fuse_seconds: number;
  private readonly on_explode: StickyMineOptions["on_explode"];
  private readonly start: THREE.Vector3;
  private readonly arc_side: number;

  private phase = StickyMinePhase.Flying;
  private elapsed = 0;
  private blink_phase = 0;
  private readonly last_target_point = new THREE.Vector3();
  private readonly last_target_normal = new THREE.Vector3( 0, 0, 1 );
  private readonly local_offset = new THREE.Vector3();
  private readonly local_rotation = new THREE.Quaternion();
  private static readonly PITCH_AXIS = new THREE.Vector3( 1, 0, 0 );
  private static readonly FACE_CAMERA = new THREE.Quaternion().setFromAxisAngle(
    StickyMine.PITCH_AXIS,
    Math.PI / 2
  );

  public constructor( options: StickyMineOptions )
  {
    this.target = options.target;
    this.fuse_seconds = options.fuse_seconds;
    this.on_explode = options.on_explode;
    this.start = options.start.clone();
    this.arc_side = options.arc_side;
    this.material = StickyMine.getSharedMaterial().clone();

    const model = ResourceManager.getModel( ResourceID.MineModel );
    const authored = new THREE.Box3().setFromObject( model );
    const authored_size = authored.getSize( new THREE.Vector3() );
    const scale = options.diameter / Math.max( authored_size.x, authored_size.z );
    model.scale.multiplyScalar( scale );

    const bounds = new THREE.Box3().setFromObject( model );
    const centre = bounds.getCenter( new THREE.Vector3() );
    // The mine lies on its back (+Y up) in the source file. Rest its bottom on
    // the wrapper origin so it sits flush once turned towards the camera.
    model.position.set( -centre.x, -bounds.min.y, -centre.z );

    model.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.material = this.material;
      child.castShadow = true;
    } );

    this.root.add( model );
    this.root.quaternion.copy( StickyMine.FACE_CAMERA );
    this.root.position.copy( this.start );
    this.root.scale.setScalar( 0.6 );
    SceneManager.world.add( this.root );

    this.last_target_point.copy( this.targetSurfacePoint() ?? this.start );
  }

  public static createWarmupProbe(): THREE.Mesh
  {
    const mesh = new THREE.Mesh( new THREE.BoxGeometry( 0.01, 0.01, 0.01 ), StickyMine.getSharedMaterial().clone() );
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    mesh.renderOrder = 10;
    mesh.userData.noBatch = true;
    return mesh;
  }

  public collectInterpolated( add: InterpolationSink ): void
  {
    if ( this.phase === StickyMinePhase.Armed )
      add( this.root, false );
  }

  private static getSharedMaterial(): THREE.MeshStandardMaterial
  {
    if ( !this.shared_material )
    {
      const orm = ResourceManager.getTexture( ResourceID.MineOrmMap );
      this.shared_material = new THREE.MeshStandardMaterial( {
        map: ResourceManager.getTexture( ResourceID.MineBaseMap ),
        normalMap: ResourceManager.getNormalMap( ResourceID.MineNormalMap ),
        roughnessMap: orm,
        metalnessMap: orm,
        roughness: 1,
        metalness: 1,
        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: ResourceManager.getTexture( ResourceID.MineEmissiveMap ),
        emissiveIntensity: StickyMine.EMISSIVE_REST,
      } );
    }
    return this.shared_material;
  }

  public isFinished(): boolean
  {
    return this.phase === StickyMinePhase.Exploded;
  }

  public isAttachedTo( entity: LevelEntity ): boolean
  {
    return this.phase !== StickyMinePhase.Exploded && this.target === entity;
  }

  public update( dt: number ): void
  {
    if ( this.phase === StickyMinePhase.Exploded )
      return;

    this.elapsed += dt;

    if ( this.phase === StickyMinePhase.Flying )
      this.updateFlight();
    else
      this.updateArmed( dt );
  }

  /** Blows the mine up right away, e.g. when its block was destroyed. */
  public detonate(): void
  {
    if ( this.phase === StickyMinePhase.Exploded )
      return;

    this.phase = StickyMinePhase.Exploded;
    const point = this.root.position.clone();
    this.dispose();
    this.on_explode( this, this.isTargetUsable() ? this.target : null, point );
  }

  public dispose(): void
  {
    this.root.removeFromParent();
    this.material.dispose();
  }

  private updateFlight(): void
  {
    const surface = this.targetSurfacePoint();
    if ( surface )
      this.last_target_point.copy( surface );

    const t = Math.min( 1, this.elapsed / StickyMine.FLIGHT_SECONDS );
    const end = this.last_target_point;
    const control_y = Math.max( this.start.y, end.y ) + StickyMine.ARC_HEIGHT;
    const inv = 1 - t;

    this.root.position.set(
      this.start.x + ( end.x - this.start.x ) * t,
      inv * inv * this.start.y + 2 * inv * t * control_y + t * t * end.y,
      this.start.z + ( end.z - this.start.z ) * t
    );

    const pitch_angle = Math.PI / 2 + ( 1 - t ) * StickyMine.FLIGHT_TURNS * Math.PI * 2;
    this.root.quaternion.setFromAxisAngle( StickyMine.PITCH_AXIS, pitch_angle );
    this.root.scale.setScalar( 0.6 + 0.4 * t );

    if ( t < 1 )
      return;

    if ( !this.isTargetUsable() )
    {
      this.detonate();
      return;
    }

    this.attachToTarget();
    this.phase = StickyMinePhase.Armed;
    this.elapsed = 0;
  }

  private updateArmed( dt: number ): void
  {
    if ( !this.isTargetUsable() )
    {
      this.detonate();
      return;
    }

    this.followTarget();

    const progress = Math.min( 1, this.elapsed / Math.max( 0.001, this.fuse_seconds ) );
    const frequency = StickyMine.BLINK_MIN_HZ
      + ( StickyMine.BLINK_MAX_HZ - StickyMine.BLINK_MIN_HZ ) * progress * progress;
    this.blink_phase += dt * frequency * Math.PI * 2;
    const pulse = Math.pow( 0.5 + 0.5 * Math.sin( this.blink_phase ), 2 );

    this.material.emissiveIntensity = StickyMine.EMISSIVE_REST
      + ( StickyMine.EMISSIVE_PEAK - StickyMine.EMISSIVE_REST ) * pulse;

    const landing = Math.min( 1, this.elapsed / StickyMine.LANDING_SQUASH_SECONDS );
    const squash = 1 - 0.25 * Math.sin( landing * Math.PI );
    const swell = 1 + StickyMine.PULSE_SCALE * pulse * progress;
    this.root.scale.set( swell, swell * squash, swell );

    if ( progress >= 1 )
      this.detonate();
  }

  private isTargetUsable(): boolean
  {
    return this.target.isAlive() && this.target.hasPhysicsBody?.() !== false;
  }

  public static frontFace( target: LevelEntity, gap: number, point: THREE.Vector3, normal: THREE.Vector3 ): void
  {
    const position = target.body.translation();
    const rotation = target.body.rotation();
    const orientation = new THREE.Quaternion( rotation.x, rotation.y, rotation.z, rotation.w );
    const local = StickyMine.FORWARD.clone().applyQuaternion( orientation.clone().invert() );

    let axis = 2;
    let distance = Infinity;
    for ( let i = 0; i < 3; i++ )
    {
      const along = Math.abs( local.getComponent( i ) );
      const half = target.size.getComponent( i ) / 2;
      if ( along > StickyMine.AXIS_EPSILON && half / along < distance )
      {
        distance = half / along;
        axis = i;
      }
    }

    normal.set( 0, 0, 0 ).setComponent( axis, Math.sign( local.getComponent( axis ) ) ).applyQuaternion( orientation );
    point.set( position.x, position.y, position.z ).addScaledVector( StickyMine.FORWARD, distance ).addScaledVector( normal, gap );
  }

  private targetSurfacePoint(): THREE.Vector3 | null
  {
    if ( !this.isTargetUsable() )
      return null;

    const point = new THREE.Vector3();
    StickyMine.frontFace( this.target, StickyMine.SURFACE_GAP, point, this.last_target_normal );
    return point;
  }

  private attachToTarget(): void
  {
    const body_position = this.targetBodyPosition();
    const body_rotation = this.targetBodyRotation();
    const inverse = body_rotation.clone().invert();

    this.root.quaternion.setFromUnitVectors( StickyMine.FORWARD, this.last_target_normal ).multiply( StickyMine.FACE_CAMERA );
    this.root.position.copy( this.last_target_point );
    this.local_offset.copy( this.root.position ).sub( body_position ).applyQuaternion( inverse );
    this.local_rotation.copy( inverse ).multiply( this.root.quaternion );
    this.root.scale.setScalar( 1 );
  }

  private followTarget(): void
  {
    const body_position = this.targetBodyPosition();
    const body_rotation = this.targetBodyRotation();

    this.root.position.copy( this.local_offset ).applyQuaternion( body_rotation ).add( body_position );
    this.root.quaternion.copy( body_rotation ).multiply( this.local_rotation );
  }

  private targetBodyPosition(): THREE.Vector3
  {
    const position = this.target.body.translation();
    return new THREE.Vector3( position.x, position.y, position.z );
  }

  private targetBodyRotation(): THREE.Quaternion
  {
    const rotation = this.target.body.rotation();
    return new THREE.Quaternion( rotation.x, rotation.y, rotation.z, rotation.w );
  }

}
